import { api } from '@/lib/http-client';
import type {
  MetaOAuthChannel,
  OAuthCallbackResult,
  OAuthPage,
  WhatsAppSignupResult,
} from '@/types/integrations';

/**
 * Módulo 2.1 — Onboarding OAuth de Meta (`docs/openapi.yaml` → tag `OAuth`,
 * documentación de negocio en `docs/ONBOARDING_OAUTH.md`).
 *
 * El flujo completo tiene tres pasos, y el del medio NO pasa por aquí: es Meta quien
 * redirige el navegador a `GET /api/integrations/oauth/meta/callback` (público), que a su
 * vez devuelve al frontend con `?oauth=connected|select|error` cuando el backend tiene
 * configurado `META_OAUTH_FRONTEND_REDIRECT`.
 *
 *  1. `startMetaOAuth` → `{ authorizeUrl }` → el navegador va a Facebook.
 *  2. (Meta → callback → frontend con el resultado en la query string).
 *  3. Si el usuario administra varias Pages: `listPendingPages` + `selectMetaPage`.
 *
 * Si al backend le faltan `META_APP_ID` / `META_APP_SECRET` / `META_OAUTH_REDIRECT_URI`,
 * `start` responde 400 con ese mensaje: la pantalla lo muestra y ofrece la carga manual.
 */

/** `GET /api/integrations/oauth/:tenantId/meta/start?channel=…` (solo ADMIN). */
export function startMetaOAuth(
  tenantId: string,
  channel: MetaOAuthChannel,
): Promise<{ authorizeUrl: string }> {
  return api.get<{ authorizeUrl: string }>(`/api/integrations/oauth/${tenantId}/meta/start`, {
    query: { channel },
  });
}

/** `GET /api/integrations/oauth/:tenantId/meta/pending/:ticket` (solo ADMIN). */
export function listPendingPages(tenantId: string, ticket: string): Promise<{ pages: OAuthPage[] }> {
  return api.get<{ pages: OAuthPage[] }>(
    `/api/integrations/oauth/${tenantId}/meta/pending/${encodeURIComponent(ticket)}`,
  );
}

/** `POST /api/integrations/oauth/:tenantId/meta/select` (solo ADMIN; cierra la conexión). */
export function selectMetaPage(
  tenantId: string,
  ticket: string,
  pageId: string,
): Promise<OAuthCallbackResult> {
  return api.post<OAuthCallbackResult>(`/api/integrations/oauth/${tenantId}/meta/select`, {
    ticket,
    pageId,
  });
}

/**
 * `POST /api/integrations/oauth/:tenantId/meta/whatsapp/complete` (solo ADMIN).
 *
 * Cierra el onboarding de WhatsApp con Embedded Signup: el navegador manda el `code` (vive 30 s),
 * el `waba_id` y el `phone_number_id` que devolvió la ventana de Meta; el backend canjea el code por
 * el token del cliente, suscribe la WABA a los webhooks del SaaS y guarda las credenciales cifradas.
 * `pin` solo hace falta si hay que registrar el número (normalmente lo registra el propio flujo).
 */
export function completeWhatsAppSignup(
  tenantId: string,
  payload: { code: string; wabaId: string; phoneNumberId: string; pin?: string },
): Promise<WhatsAppSignupResult> {
  return api.post<WhatsAppSignupResult>(
    `/api/integrations/oauth/${tenantId}/meta/whatsapp/complete`,
    payload,
  );
}
